// Minimal iCalendar (RFC 5545) parser for schedule import.
//
// Targets the Google Calendar / Apple Calendar / Outlook export
// format (.ics file). For each VEVENT we extract SUMMARY (event
// title — typically the staff member's name) plus DTSTART / DTEND
// (the shift bounds) and produce a row shape that matches the
// schedule import: { name, shift_date, start_time, end_time, role }.
//
// Limitations (intentional for MVP):
//   - No RRULE expansion (recurring events are read once at their
//     base date — manager can re-export with the recurring window
//     expanded if Google Calendar doesn't already do so).
//   - TZID parameters are ignored; Z-suffixed UTC timestamps are
//     converted to browser-local; floating times are kept as-is.
//   - Date-only events (all-day) are skipped — no times to record.
//
// If the family-friend's calendar uses unusual fields, surface
// per-row warnings and let the manager fix the source rather than
// adding heuristics that obscure errors.

export function parseIcsCalendar(text) {
  const errors = []
  const lines = unfoldLines(text)
  const events = []
  let current = null

  for (const line of lines) {
    if (line === 'BEGIN:VEVENT') {
      current = {}
      continue
    }
    if (line === 'END:VEVENT') {
      if (current) {
        events.push(current)
      }
      current = null
      continue
    }
    if (!current) continue

    // Property line: KEY[;PARAMS]:VALUE
    const colonIdx = line.indexOf(':')
    if (colonIdx === -1) continue
    const keyAndParams = line.slice(0, colonIdx)
    const value = line.slice(colonIdx + 1)
    const key = keyAndParams.split(';')[0].toUpperCase()

    if (key === 'DTSTART')      current.dtstart = value
    else if (key === 'DTEND')   current.dtend   = value
    else if (key === 'SUMMARY') current.summary = unescapeText(value)
    // (Description, location etc. ignored for this MVP)
  }

  // Convert raw events into schedule-import row shape.
  const rows = []
  for (let i = 0; i < events.length; i++) {
    const ev = events[i]
    if (!ev.summary || !ev.summary.trim()) {
      errors.push(`Event ${i + 1}: skipped — no SUMMARY (event title)`)
      continue
    }
    if (!ev.dtstart || !ev.dtend) {
      errors.push(`Event ${i + 1} ("${ev.summary}"): skipped — missing start or end time`)
      continue
    }
    const start = parseIcsDateTime(ev.dtstart)
    const end   = parseIcsDateTime(ev.dtend)
    if (!start || !end) {
      errors.push(`Event ${i + 1} ("${ev.summary}"): skipped — couldn't parse times`)
      continue
    }
    if (!start.time || !end.time) {
      errors.push(`Event ${i + 1} ("${ev.summary}"): skipped — all-day events not supported`)
      continue
    }

    rows.push({
      name:       ev.summary.trim(),
      shift_date: start.date,
      start_time: start.time,
      end_time:   end.time,
      role:       null,
    })
  }

  return { rows, errors, eventCount: events.length }
}

// ---------- Helpers ----------------------------------------

// ICS line-folding: continuation lines start with whitespace and
// belong to the previous logical line.
function unfoldLines(text) {
  const raw = text.split(/\r?\n/)
  const out = []
  for (const line of raw) {
    if (line.length === 0) continue
    if ((line[0] === ' ' || line[0] === '\t') && out.length > 0) {
      out[out.length - 1] += line.slice(1)
    } else {
      out.push(line)
    }
  }
  return out
}

// RFC 5545 text escaping: \, \; \n \\ are the four escapes.
function unescapeText(s) {
  return s
    .replace(/\\\\/g, '__BACKSLASH__')   // protect \\
    .replace(/\\,/g, ',')
    .replace(/\\;/g, ';')
    .replace(/\\n/gi, '\n')
    .replace(/__BACKSLASH__/g, '\\')
}

// Parse an ICS DATE-TIME or DATE value.
// Forms:
//   20260427T220000Z      — UTC
//   20260427T220000       — floating local time
//   20260427              — date only (no time component)
// Returns { date: 'YYYY-MM-DD', time: 'HH:MM' | null } or null.
function parseIcsDateTime(s) {
  if (!s) return null
  const trimmed = s.trim().replace(/^"|"$/g, '')
  const m = trimmed.match(/^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})(Z?))?$/)
  if (!m) return null
  const [, y, mo, d, hh, mm, ss, z] = m

  if (!hh) {
    // Date only — no time
    return { date: `${y}-${mo}-${d}`, time: null }
  }

  if (z) {
    // UTC — convert to browser-local
    const utc = new Date(Date.UTC(+y, +mo - 1, +d, +hh, +mm, +(ss || 0)))
    const yy = utc.getFullYear()
    const mm2 = String(utc.getMonth() + 1).padStart(2, '0')
    const dd  = String(utc.getDate()).padStart(2, '0')
    const HH  = String(utc.getHours()).padStart(2, '0')
    const MM  = String(utc.getMinutes()).padStart(2, '0')
    return { date: `${yy}-${mm2}-${dd}`, time: `${HH}:${MM}` }
  }

  // Floating local time — already in the user's local context
  return { date: `${y}-${mo}-${d}`, time: `${hh}:${mm}` }
}
